import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useSession } from "../../providers/SessionProvider";
import { authApi } from "../../api/auth.api";
import { toast } from "../../hooks/use-toast";
import { motion, AnimatePresence } from "framer-motion";
import { Sidebar } from "../includes/sidebar";
import { Topbar } from "../includes/topbar";
import {
    User,
    Shield,
    Key,
    Camera,
    Save,
    Loader2,
    ArrowLeft,
    Mail,
    Phone,
    Eye,
    EyeOff,
    Check,
} from "lucide-react";
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from "../../components/ui/card";
import {
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
} from "../../components/ui/tabs";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
    Avatar,
    AvatarFallback,
    AvatarImage,
} from "../../components/ui/avatar";
import { SecuritySettings } from "../../components/profileUser/SecuritySettings";
import { ImageUploadModal } from "../../components/modals/ImageUploadModal";

export default function UserProfilePage() {
    const navigate = useNavigate();
    const { user, refreshProfile, isLoading: authLoading } = useAuth();
    const { userData, refreshUserData } = useSession();

    const [activeTab, setActiveTab] = useState("profile");
    const [isLoading, setIsLoading] = useState(false);
    const [imageModalOpen, setImageModalOpen] = useState(false);

    // Page is loading until user data is available
    const pageLoading = authLoading || !user;

    // Profile form state
    const [profileForm, setProfileForm] = useState({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        username: "",
    });

    // PIN reset state
    const [pinForm, setPinForm] = useState({
        currentPin: "",
        newPin: "",
        confirmPin: "",
    });
    const [showPins, setShowPins] = useState({
        current: false,
        new: false,
        confirm: false,
    });
    const [isPinLoading, setIsPinLoading] = useState(false);

    // Initialize form with user data
    useEffect(() => {
        if (user) {
            setProfileForm({
                firstName: user.firstName || "",
                lastName: user.lastName || "",
                email: user.email || "",
                phone: user.phone || user.tel || "",
                username: user.username || "",
            });
        }
    }, [user]);

    const getInitials = () => {
        if (profileForm.firstName && profileForm.lastName) {
            return `${profileForm.firstName[0]}${profileForm.lastName[0]}`.toUpperCase();
        }
        if (user?.username) {
            return user.username.slice(0, 2).toUpperCase();
        }
        return "??";
    };

    const handleProfileChange = (field: string, value: string) => {
        setProfileForm(prev => ({ ...prev, [field]: value }));
    };

    const handleProfileSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);

        try {
            await authApi.updateProfile({
                firstName: profileForm.firstName,
                lastName: profileForm.lastName,
                email: profileForm.email,
                phone: profileForm.phone,
            });

            toast({
                title: "Siksè",
                description: "Pwofil ou mete ajou avèk siksè!",
                type: "success",
            });

            // Refresh user data
            if (refreshProfile) refreshProfile();
        } catch (error: any) {
            console.error("Profile update error:", error);
            toast({
                title: "Erè",
                description: error.response?.data?.message || "Echèk pou mete pwofil ou ajou",
                type: "error",
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handlePhotoUpdate = async (newImage: string) => {
        try {
            // First upload the image to get a URL path
            const { imagePath } = await authApi.uploadProfileImage(newImage);

            // Then update the profile with the URL
            await authApi.updateProfile({ image: imagePath });

            toast({
                title: "Siksè",
                description: "Foto pwofil ou mete ajou!",
                type: "success",
            });
            if (refreshProfile) refreshProfile();
            setImageModalOpen(false);
        } catch (error) {
            console.error("Photo update error:", error);
            toast({
                title: "Erè",
                description: "Echèk pou mete foto ou ajou",
                type: "error",
            });
        }
    };

    const handlePinChange = (field: string, value: string) => {
        // Only allow numbers and max 6 digits
        const numericValue = value.replace(/\D/g, '').slice(0, 6);
        setPinForm(prev => ({ ...prev, [field]: numericValue }));
    };

    const handlePinSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        // Validation
        if (pinForm.newPin.length !== 6) {
            toast({
                title: "Atansyon",
                description: "Nouvo PIN dwe genyen 6 chif",
                type: "warning",
            });
            return;
        }

        if (pinForm.newPin !== pinForm.confirmPin) {
            toast({
                title: "Erè",
                description: "Nouvo PIN yo pa matche",
                type: "error",
            });
            return;
        }

        setIsPinLoading(true);

        try {
            await authApi.changePin({
                currentPin: pinForm.currentPin,
                newPin: pinForm.newPin,
            });

            toast({
                title: "Siksè",
                description: "PIN ou chanje avèk siksè!",
                type: "success",
            });

            // Clear form
            setPinForm({ currentPin: "", newPin: "", confirmPin: "" });
        } catch (error: any) {
            console.error("PIN reset error:", error);
            toast({
                title: "Erè",
                description: error.response?.data?.message || "PIN aktyèl la pa kòrèk",
                type: "error",
            });
        } finally {
            setIsPinLoading(false);
        }
    };

    const togglePinVisibility = (field: 'current' | 'new' | 'confirm') => {
        setShowPins(prev => ({ ...prev, [field]: !prev[field] }));
    };

    return (
        <div className="flex h-screen overflow-hidden">
            <Sidebar />
            <div className="flex-1 flex flex-col overflow-hidden">
                <Topbar />
                <div className="flex-1 overflow-y-auto p-8 pt-6">
                    {/* Header */}
                    <div className="flex items-center gap-4 mb-6">
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => navigate(-1)}
                            className="shrink-0"
                        >
                            <ArrowLeft className="h-5 w-5" />
                        </Button>
                        <div>
                            <h1 className="text-2xl font-bold tracking-tight">Paramèt Pwofil</h1>
                            <p className="text-muted-foreground">
                                Jere enfomasyon kont ou ak sekirite
                            </p>
                        </div>
                    </div>

                    {/* Loading Skeleton */}
                    {pageLoading ? (
                        <div className="space-y-6 animate-pulse">
                            {/* Tabs skeleton */}
                            <div className="flex gap-2 max-w-md">
                                <div className="h-10 bg-muted rounded-md flex-1"></div>
                                <div className="h-10 bg-muted rounded-md flex-1"></div>
                                <div className="h-10 bg-muted rounded-md flex-1"></div>
                            </div>
                            {/* Card skeleton */}
                            <div className="border rounded-lg p-6 space-y-6">
                                <div className="space-y-2">
                                    <div className="h-6 bg-muted rounded w-1/4"></div>
                                    <div className="h-4 bg-muted rounded w-1/2"></div>
                                </div>
                                <div className="flex items-center gap-6">
                                    <div className="h-24 w-24 bg-muted rounded-full"></div>
                                    <div className="space-y-2">
                                        <div className="h-5 bg-muted rounded w-32"></div>
                                        <div className="h-4 bg-muted rounded w-24"></div>
                                    </div>
                                </div>
                                <div className="grid gap-4 md:grid-cols-2">
                                    <div className="space-y-2">
                                        <div className="h-4 bg-muted rounded w-16"></div>
                                        <div className="h-10 bg-muted rounded"></div>
                                    </div>
                                    <div className="space-y-2">
                                        <div className="h-4 bg-muted rounded w-16"></div>
                                        <div className="h-10 bg-muted rounded"></div>
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <div className="h-4 bg-muted rounded w-16"></div>
                                    <div className="h-10 bg-muted rounded"></div>
                                </div>
                                <div className="space-y-2">
                                    <div className="h-4 bg-muted rounded w-16"></div>
                                    <div className="h-10 bg-muted rounded"></div>
                                </div>
                            </div>
                        </div>
                    ) : (
                        /* Tabs */
                        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
                            <TabsList className="grid w-full max-w-md grid-cols-3">
                                <TabsTrigger value="profile" className="flex items-center gap-2">
                                    <User className="h-4 w-4" />
                                    Pwofil
                                </TabsTrigger>
                                <TabsTrigger value="security" className="flex items-center gap-2">
                                    <Shield className="h-4 w-4" />
                                    Sekirite
                                </TabsTrigger>
                                <TabsTrigger value="pin" className="flex items-center gap-2">
                                    <Key className="h-4 w-4" />
                                    PIN
                                </TabsTrigger>
                            </TabsList>

                            {/* Profile Tab */}
                            <TabsContent value="profile" className="space-y-6">
                                <Card>
                                    <CardHeader>
                                        <CardTitle>Enfomasyon Pèsonèl</CardTitle>
                                        <CardDescription>
                                            Mete ajou enfomasyon pwofil ou isit la
                                        </CardDescription>
                                    </CardHeader>
                                    <CardContent>
                                        <form onSubmit={handleProfileSubmit} className="space-y-6">
                                            {/* Avatar Section */}
                                            <div className="flex items-center gap-6">
                                                <div className="relative group">
                                                    <Avatar className="h-24 w-24 border-4 border-background shadow-lg">
                                                        <AvatarImage src={user?.image || undefined} />
                                                        <AvatarFallback className="text-2xl bg-primary text-primary-foreground">
                                                            {getInitials()}
                                                        </AvatarFallback>
                                                    </Avatar>
                                                    <button
                                                        type="button"
                                                        onClick={() => setImageModalOpen(true)}
                                                        className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                                                    >
                                                        <Camera className="h-8 w-8 text-white" />
                                                    </button>
                                                </div>
                                                <div>
                                                    <h3 className="font-semibold text-lg">
                                                        {profileForm.firstName} {profileForm.lastName}
                                                    </h3>
                                                    <p className="text-sm text-muted-foreground">
                                                        @{profileForm.username}
                                                    </p>
                                                    <Button
                                                        type="button"
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={() => setImageModalOpen(true)}
                                                        className="mt-2"
                                                    >
                                                        <Camera className="h-4 w-4 mr-2" />
                                                        Chanje foto
                                                    </Button>
                                                </div>
                                            </div>

                                            {/* Form Fields */}
                                            <div className="grid gap-4 md:grid-cols-2">
                                                <div className="space-y-2">
                                                    <Label htmlFor="firstName">Prenon</Label>
                                                    <Input
                                                        id="firstName"
                                                        value={profileForm.firstName}
                                                        onChange={(e) => handleProfileChange("firstName", e.target.value)}
                                                        placeholder="Prenon ou"
                                                    />
                                                </div>
                                                <div className="space-y-2">
                                                    <Label htmlFor="lastName">Non</Label>
                                                    <Input
                                                        id="lastName"
                                                        value={profileForm.lastName}
                                                        onChange={(e) => handleProfileChange("lastName", e.target.value)}
                                                        placeholder="Non ou"
                                                    />
                                                </div>
                                            </div>

                                            <div className="space-y-2">
                                                <Label htmlFor="email">Imèl</Label>
                                                <div className="relative">
                                                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                                    <Input
                                                        id="email"
                                                        type="email"
                                                        value={profileForm.email}
                                                        onChange={(e) => handleProfileChange("email", e.target.value)}
                                                        placeholder="imel@egzanp.com"
                                                        className="pl-10"
                                                    />
                                                </div>
                                            </div>

                                            <div className="space-y-2">
                                                <Label htmlFor="phone">Telefòn</Label>
                                                <div className="relative">
                                                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                                    <Input
                                                        id="phone"
                                                        type="tel"
                                                        value={profileForm.phone}
                                                        onChange={(e) => handleProfileChange("phone", e.target.value)}
                                                        placeholder="+509 0000 0000"
                                                        className="pl-10"
                                                    />
                                                </div>
                                            </div>

                                            <div className="flex justify-end">
                                                <Button type="submit" disabled={isLoading}>
                                                    {isLoading ? (
                                                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                                    ) : (
                                                        <Save className="h-4 w-4 mr-2" />
                                                    )}
                                                    Anrejistre
                                                </Button>
                                            </div>
                                        </form>
                                    </CardContent>
                                </Card>
                            </TabsContent>

                            {/* Security Tab */}
                            <TabsContent value="security" className="space-y-6">
                                <SecuritySettings />
                            </TabsContent>

                            {/* PIN Tab */}
                            <TabsContent value="pin" className="space-y-6">
                                <Card>
                                    <CardHeader>
                                        <CardTitle>Chanje PIN</CardTitle>
                                        <CardDescription>
                                            Kreye yon nouvo PIN 6 chif pou kont ou
                                        </CardDescription>
                                    </CardHeader>
                                    <CardContent>
                                        <form onSubmit={handlePinSubmit} className="space-y-6 max-w-md">
                                            <div className="space-y-2">
                                                <Label htmlFor="currentPin">PIN Aktyèl</Label>
                                                <div className="relative">
                                                    <Input
                                                        id="currentPin"
                                                        type={showPins.current ? "text" : "password"}
                                                        value={pinForm.currentPin}
                                                        onChange={(e) => handlePinChange("currentPin", e.target.value)}
                                                        placeholder="••••••"
                                                        className="font-mono text-center text-lg tracking-widest pr-10"
                                                        maxLength={6}
                                                    />
                                                    <button
                                                        type="button"
                                                        onClick={() => togglePinVisibility("current")}
                                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                                                    >
                                                        {showPins.current ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                                    </button>
                                                </div>
                                            </div>

                                            <div className="space-y-2">
                                                <Label htmlFor="newPin">Nouvo PIN</Label>
                                                <div className="relative">
                                                    <Input
                                                        id="newPin"
                                                        type={showPins.new ? "text" : "password"}
                                                        value={pinForm.newPin}
                                                        onChange={(e) => handlePinChange("newPin", e.target.value)}
                                                        placeholder="••••••"
                                                        className="font-mono text-center text-lg tracking-widest pr-10"
                                                        maxLength={6}
                                                    />
                                                    <button
                                                        type="button"
                                                        onClick={() => togglePinVisibility("new")}
                                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                                                    >
                                                        {showPins.new ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                                    </button>
                                                </div>
                                                <p className="text-xs text-muted-foreground">
                                                    PIN dwe genyen egzakteman 6 chif
                                                </p>
                                            </div>

                                            <div className="space-y-2">
                                                <Label htmlFor="confirmPin">Konfime Nouvo PIN</Label>
                                                <div className="relative">
                                                    <Input
                                                        id="confirmPin"
                                                        type={showPins.confirm ? "text" : "password"}
                                                        value={pinForm.confirmPin}
                                                        onChange={(e) => handlePinChange("confirmPin", e.target.value)}
                                                        placeholder="••••••"
                                                        className={`font-mono text-center text-lg tracking-widest pr-10 ${pinForm.confirmPin && pinForm.newPin === pinForm.confirmPin
                                                            ? "border-green-500 focus-visible:ring-green-500"
                                                            : ""
                                                            }`}
                                                        maxLength={6}
                                                    />
                                                    <button
                                                        type="button"
                                                        onClick={() => togglePinVisibility("confirm")}
                                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                                                    >
                                                        {showPins.confirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                                    </button>
                                                    {pinForm.confirmPin && pinForm.newPin === pinForm.confirmPin && (
                                                        <Check className="absolute right-10 top-1/2 -translate-y-1/2 h-4 w-4 text-green-500" />
                                                    )}
                                                </div>
                                            </div>

                                            <div className="flex justify-end pt-4">
                                                <Button
                                                    type="submit"
                                                    disabled={isPinLoading || pinForm.newPin.length !== 6 || pinForm.newPin !== pinForm.confirmPin}
                                                >
                                                    {isPinLoading ? (
                                                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                                    ) : (
                                                        <Key className="h-4 w-4 mr-2" />
                                                    )}
                                                    Chanje PIN
                                                </Button>
                                            </div>
                                        </form>
                                    </CardContent>
                                </Card>
                            </TabsContent>
                        </Tabs>
                    )}
                </div>
            </div>

            {/* Image Upload Modal */}
            <ImageUploadModal
                isOpen={imageModalOpen}
                onClose={() => setImageModalOpen(false)}
                onUpload={handlePhotoUpdate}
            />
        </div>
    );
}
